import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Globe2, TrendingUp, Users, BookOpen, 
  Building2, Sparkles, CheckCircle2, ExternalLink,
  Home, Scale, Lightbulb, Quote, FileText, DollarSign,
  MapPin, BarChart3, GraduationCap, Award, Target
} from 'lucide-react';

const NexusImpactPage = () => {
  // Research-backed statistics
  const researchStats = [
    { value: '$295M', label: 'US Jock Tax Revenue (2024)', source: 'National Taxpayers Union Foundation' },
    { value: '1.6B', label: 'People Lacking Adequate Housing', source: 'UN-Habitat' },
    { value: '96,000', label: 'Homes Needed Daily by 2030', source: 'UN-Habitat' },
    { value: '54%', label: 'US Housing Price Increase (2015-2024)', source: 'OECD' }
  ];

  // Athlete examples
  const athleteExamples = [
    {
      name: 'LeBron James',
      initiative: 'I PROMISE Housing',
      impact: '50 affordable housing units in Akron, Ohio',
      description: 'A fifty-unit affordable housing complex featuring apartments with fiber-optic Wi-Fi and community facilities.'
    },
    {
      name: 'Taylor Swift',
      initiative: 'Housing Security Support',
      impact: '$100,000+ to Habitat for Humanity',
      description: 'Significant donations to disaster relief efforts and housing assistance for communities in need.'
    },
    {
      name: 'Legacy Foundation UK',
      initiative: 'Rio Ferdinand, Bobby Zamora, Mark Noble',
      impact: '£400M development, 1,300 homes',
      description: 'Former Premier League players established a foundation funding major housing developments including social housing.'
    }
  ];

  // Global housing models
  const globalModels = [
    {
      country: 'Austria',
      model: 'Dedicated Housing Tax',
      description: 'Austrians pay ~1% of income as housing tax. Creates Gemeindebauten (communal buildings) fully owned by cities.',
      flag: '🇦🇹'
    },
    {
      country: 'Japan',
      model: 'Form-Based Zoning',
      description: 'Nested zoning system with centrally regulated, uniform nationwide permitting keeps housing affordable even in Tokyo.',
      flag: '🇯🇵'
    },
    {
      country: 'Spain',
      model: 'The "Beckham Law"',
      description: 'Foreign players taxed at 24.75% (vs 40%+), attracting international talent while generating substantial state revenue.',
      flag: '🇪🇸'
    }
  ];

  // Research partners
  const researchPartners = [
    {
      name: 'Harvard Weatherhead Center',
      description: 'Center for International Affairs',
      logo: null, // Will use text-based logo
      type: 'text',
      initial: 'W'
    },
    {
      name: 'Harvard Joint Center for Housing',
      description: 'Housing Studies',
      logo: null,
      type: 'text',
      initial: 'JCHS'
    },
    {
      name: 'MIT Media Lab, City Science',
      description: 'Media Lab Research Group',
      logo: null,
      type: 'text',
      initial: 'MIT'
    },
    {
      name: 'Linneman Associates',
      description: 'Real Estate Research',
      logo: null,
      type: 'text',
      initial: 'LA'
    },
    {
      name: 'Global InTech',
      description: 'Technology Partner',
      logoDark: 'https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/opw5twe6_final_logo_dark_transparent.png',
      logoLight: 'https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/azd2bq53_globalintech_logo_light.png',
      type: 'image'
    },
    {
      name: 'Morency QSI Framework',
      description: 'Quantum Social Impact',
      logoDark: 'https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/ak0ll9ug_Morency_QSI_Framework_Logo_White.jpg',
      logoLight: 'https://customer-assets.emergentagent.com/job_e3d4135c-91f6-4c21-8964-e67cd3b65643/artifacts/qrk7fo0z_Morency_QSI_Framework_Logo_Dark.jpg',
      type: 'image'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero - Research Focus */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
            <GraduationCap className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium">Harvard Weatherhead Center Research Initiative</span>
          </div>
          
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="research-title">
            How Athletes' Taxes Can Solve the{' '}
            <span className="text-gradient-housing">Global Housing Crisis</span>
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            A research initiative exploring how the billions in international athlete and entertainer 
            taxation can be systematically redirected toward solving the world's affordable housing shortage.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#research"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
              data-testid="explore-research-btn"
            >
              Explore the Research
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="https://epicenter.wcfia.harvard.edu/article/how-athletes-taxes-can-solve-affordable-housing-crisis"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
            >
              Read the Harvard Article
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Research Partners */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="research-partners">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-medium">
              Research Partners & Affiliations
            </span>
          </div>
          <div className="glass-card rounded-2xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
              {researchPartners.map((partner, index) => (
                <div key={index} className="flex flex-col items-center text-center group">
                  {partner.type === 'image' ? (
                    <div className="h-16 flex items-center justify-center mb-2">
                      <img 
                        src={partner.logoDark} 
                        alt={partner.name}
                        className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:block hidden"
                      />
                      <img 
                        src={partner.logoLight} 
                        alt={partner.name}
                        className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:hidden block"
                      />
                    </div>
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-2 group-hover:from-primary/30 group-hover:to-secondary/30 transition-all">
                      <span className="font-outfit font-bold text-lg text-foreground">{partner.initial}</span>
                    </div>
                  )}
                  <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    {partner.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Statistics */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="research-stats">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <h2 className="font-outfit text-2xl font-semibold text-center mb-8">
              The <span className="text-gradient-quantum">Evidence</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {researchStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="font-outfit text-3xl md:text-4xl font-bold text-gradient-quantum mb-2">
                    {stat.value}
                  </div>
                  <div className="font-medium text-sm mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.source}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Hidden Revenue Stream */}
      <section id="research" className="px-6 md:px-12 lg:px-24 mb-20 scroll-mt-24" data-testid="hidden-revenue">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
                The Core Thesis
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
                The <span className="text-gradient-housing">Hidden Revenue Stream</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                International athlete and entertainer taxation systems generate billions in revenue annually. 
                When strategically leveraged through public-private partnerships, this international revenue 
                stream could represent tens of billions in development capital for addressing the global housing crisis.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">OECD Article 17:</strong>
                    <span className="text-muted-foreground"> Foundation for international athlete taxation across borders</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">The "Twelve-Day Rule" Exception:</strong>
                    <span className="text-muted-foreground"> Athletes taxed from day one in every jurisdiction</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-foreground">Dr. Harris's Research:</strong>
                    <span className="text-muted-foreground"> The "shut up and dribble tax" lacks transparency</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="glass-card rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
              <div className="relative z-10">
                <Quote className="w-10 h-10 text-primary mb-4" />
                <blockquote className="text-lg italic mb-4">
                  "Unlike, say, nurse practitioners or graphic designers, jurisdictions know exactly 
                  when athletes are in town. They know how much they make. And they know how to go after them."
                </blockquote>
                <p className="text-sm text-muted-foreground">— The Hustle</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Housing Crisis */}
      <section className="px-6 md:px-12 lg:px-24 mb-20 bg-muted/30 py-20 -mx-6 md:-mx-12 lg:-mx-24" data-testid="housing-crisis">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              The Problem
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              The <span className="text-destructive">Global Housing Crisis</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              The housing affordability crisis has become a defining challenge of our time, 
              affecting developed and developing nations alike.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="glass-card rounded-xl p-6 text-center">
              <Home className="w-10 h-10 text-destructive mx-auto mb-4" />
              <div className="text-4xl font-outfit font-bold text-destructive mb-2">1.6B</div>
              <p className="text-sm text-muted-foreground">People lack adequate housing worldwide</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center">
              <TrendingUp className="w-10 h-10 text-secondary mx-auto mb-4" />
              <div className="text-4xl font-outfit font-bold text-secondary mb-2">3B</div>
              <p className="text-sm text-muted-foreground">Will need housing by 2030 if trends continue</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center">
              <Building2 className="w-10 h-10 text-primary mx-auto mb-4" />
              <div className="text-4xl font-outfit font-bold text-primary mb-2">96K</div>
              <p className="text-sm text-muted-foreground">Affordable homes needed daily to meet demand</p>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-8 text-center">
            <Quote className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
            <blockquote className="text-xl italic mb-4 max-w-3xl mx-auto">
              "Housing affordability has deteriorated dramatically across major economies since the pandemic."
            </blockquote>
            <p className="text-sm text-muted-foreground">— International Monetary Fund</p>
          </div>
        </div>
      </section>

      {/* Athletes Leading Change */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="athletes-leading">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              From Philanthropy to Policy
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Athletes <span className="text-gradient-quantum">Leading Change</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              High-profile athletes and entertainers have created housing initiatives that demonstrate 
              what is possible when resources are directed toward community needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {athleteExamples.map((athlete, index) => (
              <div key={index} className="glass-card rounded-xl p-6 hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-1">{athlete.name}</h3>
                <p className="text-primary font-medium text-sm mb-3">{athlete.initiative}</p>
                <p className="text-secondary font-semibold mb-3">{athlete.impact}</p>
                <p className="text-muted-foreground text-sm">{athlete.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center glass-card rounded-2xl p-8">
            <Lightbulb className="w-10 h-10 text-secondary mx-auto mb-4" />
            <p className="text-lg italic max-w-3xl mx-auto">
              "If individual athletes can recognize the transformative power of housing investment, 
              systematic direction of athlete tax revenue represents an unprecedented opportunity."
            </p>
          </div>
        </div>
      </section>

      {/* Global Solutions */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="global-solutions">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Models That Work
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold">
              Global <span className="text-gradient-housing">Solutions</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {globalModels.map((model, index) => (
              <div key={index} className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{model.flag}</span>
                  <div>
                    <h3 className="font-outfit text-xl font-bold">{model.country}</h3>
                    <p className="text-primary text-sm font-medium">{model.model}</p>
                  </div>
                </div>
                <p className="text-muted-foreground">{model.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mandela Quote */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-12 bg-gradient-to-br from-primary/10 to-secondary/10 text-center">
            <Sparkles className="w-10 h-10 text-secondary mx-auto mb-6" />
            <blockquote className="text-xl md:text-2xl font-outfit italic mb-6">
              "Sport has the power to change the world. It has the power to unite in a way 
              that little else does. It speaks to youth in a language they understand. 
              Sport can create hope where once there was only despair."
            </blockquote>
            <p className="text-muted-foreground">— Nelson Mandela, Laureus World Sports Awards, 2000</p>
          </div>
        </div>
      </section>

      {/* Call to Action - Conference & Research */}
      <section className="px-6 md:px-12 lg:px-24 mb-20" data-testid="call-to-action">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Mobilizing for Change
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold">
              Call to <span className="text-gradient-quantum">Action</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Scale, title: 'Policymakers', action: 'Create transparent reporting frameworks for athlete tax allocation' },
              { icon: Users, title: 'Athletes & Entertainers', action: 'Advocate for tax allocation transparency in communities' },
              { icon: MapPin, title: 'Communities', action: 'Demand accountability for entertainment-generated revenue' },
              { icon: Globe2, title: 'Sports Organizations', action: 'Coordinate through FIFA, NFL, NBA, IOC for systematic change' }
            ].map((cta, index) => (
              <div key={index} className="glass-card rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
                  <cta.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-lg font-bold mb-2">{cta.title}</h3>
                <p className="text-sm text-muted-foreground">{cta.action}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Author & Conference CTA */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <img
                src="https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/ckxv90gi_vernand-morency.jpg"
                alt="Vernand Morency"
                className="w-32 h-32 rounded-full object-cover"
              />
              <div className="text-center md:text-left flex-1">
                <h3 className="font-outfit text-2xl font-bold mb-2">Vernand Morency</h3>
                <p className="text-primary font-medium mb-2">2025 Global Sports Fellow</p>
                <p className="text-muted-foreground text-sm mb-4">
                  Weatherhead Research Cluster on Global History, Harvard University. 
                  Former NFL & MLB professional athlete, tech entrepreneur, and AI expert.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
                  >
                    Attend the Conference
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/vernand-morency"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
                  >
                    Full Biography
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="px-6 md:px-12 lg:px-24 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            A Harvard Weatherhead Center Research Initiative •{' '}
            <a 
              href="https://epicenter.wcfia.harvard.edu/article/how-athletes-taxes-can-solve-affordable-housing-crisis"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Read the full research article
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default NexusImpactPage;
